/**
 * Org selection from the URL (MULTI-ORG-PORTAL follow-up). The app writes the
 * active org into the path as `/{org}/{site}/{view}` (App.tsx `buildPath`), so
 * a typed or bookmarked URL naming a DIFFERENT org the user belongs to is an
 * explicit org choice — OrgContext consumes it at page load, ahead of the
 * saved choice.
 *
 * Read once per page load, deliberately: after boot, App's URL-sync effect
 * canonicalizes the path from the session org, so reacting to in-app pathname
 * changes here would make the two effects fight. Mid-session switching stays
 * with the picker (`selectOrg`); a URL switch is a fresh navigation.
 */

import { resolveOrgSession } from './orgSession';

/** Memberships as `/api/my-organizations` reports them (role not needed here). */
export type OrgRef = { id: string; name: string };

/** Path segments that can never be an org (other top-level routes). */
const NON_ORG_SEGMENTS = new Set(['orient', 'callback', 'login']);

/**
 * URL segment an org slug renders as: `minetwin-default` → `minetwin`, other
 * orgs shed the stamp prefix (`minetwin-south32` → `south32`). Single source
 * of truth — App.tsx builds paths from this, and the matcher below inverts it.
 */
export function urlSlugFromOrgSlug(orgSlug: string): string {
  return orgSlug.replace(/-default$/, '').replace(/^minetwin-/, '') || orgSlug;
}

/** First path segment of `pathname`, or null when it can't be an org segment. */
export function urlOrgSegment(pathname: string): string | null {
  const seg = pathname.replace(/^\/+/, '').split('/')[0] ?? '';
  if (!seg || NON_ORG_SEGMENTS.has(seg.toLowerCase())) return null;
  return seg.toLowerCase();
}

/**
 * The URL-org intent is stashed in sessionStorage at page load rather than
 * read live, because sign-in can HARD-REDIRECT before OrgContext ever runs:
 * a signed-out `@minetwin.ai` user loading `/south32/…` bounces out to Entra
 * and back through `/callback`, and the original path is gone by the time the
 * org selection effect sees a pathname. sessionStorage survives that round
 * trip within the tab (and stays per-tab, so parallel tabs in different orgs
 * don't cross-talk). Signed-in sessions (e.g. arvikasoft accounts, which
 * never redirect) hit the same stash on the same load — one code path.
 */
const URL_ORG_INTENT_KEY = 'mt:urlOrgIntent';
/** An intent older than this is an abandoned tab, not a navigation. */
const URL_ORG_INTENT_TTL_MS = 10 * 60 * 1000;

function stashUrlOrgIntent(pathname: string): void {
  const seg = urlOrgSegment(pathname);
  if (!seg) return; // /callback, /orient/…, or no segment — never overwrite
  try {
    sessionStorage.setItem(URL_ORG_INTENT_KEY, JSON.stringify({ seg, ts: Date.now() }));
  } catch {
    // Storage unavailable — URL switching degrades to same-load-only.
  }
}

/** One-shot read of the stashed intent (cleared on read; TTL-guarded). */
export function consumeUrlOrgIntent(): string | null {
  try {
    const raw = sessionStorage.getItem(URL_ORG_INTENT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(URL_ORG_INTENT_KEY);
    const v = JSON.parse(raw) as { seg?: unknown; ts?: unknown };
    if (typeof v.seg !== 'string' || typeof v.ts !== 'number') return null;
    return Date.now() - v.ts <= URL_ORG_INTENT_TTL_MS ? v.seg : null;
  } catch {
    return null;
  }
}

// Capture at module evaluation = once per real page load, before any auth
// redirect can leave the page. SPA navigations never re-run this, which is
// exactly the contract: in-app pathname changes are App's canonicalizer.
if (typeof window !== 'undefined') {
  stashUrlOrgIntent(window.location.pathname);
}

/**
 * Resolve a URL org segment to a membership's WorkOS org id, or null when it
 * matches nothing (unknown segment, or ambiguous — both are "leave the
 * selection alone", never an error).
 *
 * Match order:
 *   1. literal WorkOS org id (`org_…`) — robustness, not a shape we emit;
 *   2. normalized org NAME (`South32` → `south32`) — no network, covers the
 *      common case where the WorkOS name reads like the slug;
 *   3. canonical: each membership's resolved slug via `get_org_info`
 *      (sessionStorage-cached; any authed JWT may resolve any org id), matched
 *      through `urlSlugFromOrgSlug` — this is exactly the segment App writes.
 *      Unprovisioned orgs fail to resolve and are skipped, not fatal.
 */
export async function orgIdFromUrlSegment(
  segment: string,
  memberships: ReadonlyArray<OrgRef>,
  token: string,
  httpBaseUri: string,
): Promise<string | null> {
  if (segment.startsWith('org_')) {
    return memberships.find((m) => m.id.toLowerCase() === segment)?.id ?? null;
  }

  const wanted = segment.replace(/[^a-z0-9]/g, '');
  if (!wanted) return null;
  const byName = memberships.filter(
    (m) => m.name.toLowerCase().replace(/[^a-z0-9]/g, '') === wanted,
  );
  if (byName.length === 1) return byName[0].id;

  const resolved = await Promise.all(
    memberships.map(async (m) => {
      try {
        const org = await resolveOrgSession(m.id, token, httpBaseUri);
        return { id: m.id, urlSlug: urlSlugFromOrgSlug(org.slug) };
      } catch {
        return null; // unprovisioned / unresolvable — can't be the URL's org
      }
    }),
  );
  const bySlug = resolved.filter(
    (r): r is { id: string; urlSlug: string } => r !== null && r.urlSlug === segment,
  );
  return bySlug.length === 1 ? bySlug[0].id : null;
}
